import { useState, useEffect, useMemo, useRef } from 'react';
import { AlertCircle, CheckCircle2, FileSearch, Gauge, Loader2, SkipForward, XCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { billService } from '../../services/billService';
import { billEmailService } from '../../services/billEmailService';
import { bulkReportService } from '../../services/bulkReportService';
import { planParse, planRun, type Skip } from '../../lib/boardActions';
import { runPool } from '../../utils/runPool';
import type { ParseState } from '../../utils/parseState';
import type { BvBill } from '../../types/billTypes';

export type BoardAction = 'parse' | 'run';

export type BoardActionRow = { bill: BvBill; parseState: ParseState; emailIds: string[] };

type BoardActionDialogProps = {
  action: BoardAction;
  rows: BoardActionRow[];
  /** Reload the board. `changed` — something was started or finished. */
  onClose: (changed: boolean) => void;
};

type Phase = 'confirm' | 'working' | 'done';

const POLL_MS = 3000;

/**
 * The board's two bulk actions, each confirm → working → done.
 *
 * Parse invoices: inbox bills go to the inbox re-parse pool; hand-uploaded
 * invoices are parsed one request per bill, three at a time.
 *
 * Run bill run: usage data, network charges, retail TOU and the calculation on the server, now. Bills
 * whose usage data does not cover their period come back skipped with that
 * reason and are not calculated; the rest are polled through the run log until
 * the run finishes.
 */
export const BoardActionDialog = ({ action, rows, onClose }: BoardActionDialogProps) => {
  const [phase, setPhase] = useState<Phase>('confirm');
  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<Skip[]>([]);
  const [summary, setSummary] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; failed: number; total: number } | null>(null);
  const [itemErrors, setItemErrors] = useState<Skip[]>([]);
  const cancelled = useRef(false);

  useEffect(() => () => { cancelled.current = true; }, []);

  const byId = useMemo(() => new Map(rows.map((r) => [r.bill.id, r.bill])), [rows]);
  const parsePlan = useMemo(() => (action === 'parse' ? planParse(rows) : null), [action, rows]);
  const runPlan = useMemo(() => (action === 'run' ? planRun(rows.map((r) => r.bill)) : null), [action, rows]);

  const willRun = parsePlan
    ? parsePlan.inboxBillIds.length + parsePlan.direct.length
    : runPlan?.billIds.length ?? 0;
  const planSkipped = parsePlan?.skipped ?? runPlan?.skipped ?? [];

  const label = (billId: string) => {
    const b = byId.get(billId);
    return b ? `${b.site?.client?.name ?? '—'} · ${b.site?.nmi ?? '—'}` : billId;
  };

  const runParse = async () => {
    if (!parsePlan) return;
    const notes: string[] = [];
    if (parsePlan.inboxEmailIds.length > 0) {
      const res = await billEmailService.retrySelected(parsePlan.inboxEmailIds);
      if (!res.success) throw new Error(res.error ?? 'Could not start the inbox re-parse');
      notes.push(`${parsePlan.inboxBillIds.length} inbox bill${parsePlan.inboxBillIds.length === 1 ? '' : 's'} queued for parsing — they finish in the background; Refresh to see them.`);
    }
    if (parsePlan.direct.length > 0) {
      let done = 0;
      const failures: Skip[] = [];
      setProgress({ done: 0, failed: 0, total: parsePlan.direct.length });
      await runPool(parsePlan.direct, 3, async ({ billId, storagePath }) => {
        const res = await billService.parseInvoice(billId, storagePath);
        if (res.success) done++;
        else failures.push({ billId, reason: res.error ?? 'Parse failed' });
        if (!cancelled.current) setProgress({ done, failed: failures.length, total: parsePlan.direct.length });
      }, (item, err) => {
        failures.push({ billId: item.billId, reason: err instanceof Error ? err.message : 'Parse failed' });
      });
      setItemErrors(failures);
      notes.push(`${done} uploaded invoice${done === 1 ? '' : 's'} parsed${failures.length ? `, ${failures.length} failed` : ''}.`);
    }
    setSkipped(parsePlan.skipped);
    setSummary(notes.join(' '));
  };

  const runNem = async () => {
    if (!runPlan) return;
    const res = await bulkReportService.runBillsNow(runPlan.billIds);
    if (!res.success || !res.data) throw new Error(res.error ?? 'Could not start the run');
    const { runId, started, skipped: serverSkipped } = res.data;
    setSkipped([...runPlan.skipped, ...serverSkipped.map((s) => ({ billId: s.bill_id, reason: s.reason }))]);
    if (!runId || started.length === 0) {
      setSummary('Nothing was calculated — every selected bill was skipped (see why below).');
      return;
    }
    setProgress({ done: 0, failed: 0, total: started.length });
    // Poll the run log until the run closes. Bills run one at a time.
    for (;;) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      if (cancelled.current) return;
      const p = await bulkReportService.runProgress(runId);
      if (!p.success || !p.data) continue;
      const mine = p.data.items.filter((i) => started.includes(i.bill_id));
      const done = mine.filter((i) => i.outcome === 'done').length;
      const errs = mine.filter((i) => i.outcome === 'error');
      setProgress({ done, failed: errs.length, total: started.length });
      setItemErrors(errs.map((i) => ({ billId: i.bill_id, reason: i.reason ?? 'Failed' })));
      if (p.data.status && p.data.status !== 'running') {
        setSummary(`${done} bill${done === 1 ? '' : 's'} calculated${errs.length ? `, ${errs.length} failed` : ''}. Reports were created for those with a parsed invoice.`);
        return;
      }
    }
  };

  const start = async () => {
    setPhase('working');
    setError(null);
    try {
      if (action === 'parse') await runParse();
      else await runNem();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      if (!cancelled.current) setPhase('done');
    }
  };

  const title = action === 'parse' ? 'Parse invoices' : 'Run bill run';
  const Icon = action === 'parse' ? FileSearch : Gauge;

  return (
    <Dialog open onOpenChange={(open) => { if (!open && phase !== 'working') onClose(phase === 'done'); }}>
      <DialogContent data-testid="board-action-dialog" className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Icon className="h-4 w-4" /> {title} — {rows.length} selected
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4 text-sm">
          {phase === 'confirm' && (
            <p className="text-gray-700">
              {action === 'parse'
                ? <>Parses <strong>{willRun}</strong> invoice{willRun === 1 ? '' : 's'}: inbox bills are re-parsed from their email, hand-uploaded invoices directly.</>
                : <>Runs the bill run (usage data, network charges, retail TOU, then the calculation) for <strong>{willRun}</strong> bill{willRun === 1 ? '' : 's'}.
                   {' '}A bill whose usage data does not cover its whole period stops at usage data — it is <strong>not calculated</strong> and says why.</>}
            </p>
          )}

          {phase === 'working' && (
            <p className="flex items-center gap-2 text-gray-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              {action === 'parse' ? 'Parsing…' : 'Running the bill run…'}
            </p>
          )}

          {progress && (
            <div data-testid="board-action-progress">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>{progress.done + progress.failed} of {progress.total}</span>
                <span>{progress.done} done · {progress.failed} failed</span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress.total ? ((progress.done + progress.failed) / progress.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}

          {summary && (
            <p className="flex gap-2 items-start rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> {summary}
            </p>
          )}
          {error && (
            <p className="flex gap-2 items-start rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /> {error}
            </p>
          )}

          <ReasonList title="Failed" icon={<XCircle className="h-3.5 w-3.5 text-red-600" />} items={itemErrors} label={label} tone="red" />
          <ReasonList
            title={phase === 'confirm' ? 'Will be skipped' : 'Skipped'}
            icon={<SkipForward className="h-3.5 w-3.5 text-gray-500" />}
            items={phase === 'confirm' ? planSkipped : skipped}
            label={label}
            tone="gray"
          />
        </div>

        <div className="px-6 py-3 border-t flex justify-end gap-2">
          {phase === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
              <Button data-testid="board-action-start" onClick={() => void start()} disabled={willRun === 0} className="gap-1.5">
                <Icon className="h-4 w-4" /> {action === 'parse' ? `Parse ${willRun}` : `Run ${willRun}`}
              </Button>
            </>
          )}
          {phase === 'working' && <Button variant="outline" disabled>Working…</Button>}
          {phase === 'done' && <Button onClick={() => onClose(true)}>Close</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

type ReasonListProps = {
  title: string;
  icon: React.ReactNode;
  items: Skip[];
  label: (billId: string) => string;
  tone: 'red' | 'gray';
};

const ReasonList = ({ title, icon, items, label, tone }: ReasonListProps) => {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{title} ({items.length})</p>
      <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100 rounded-md border border-gray-100">
        {items.map((s) => (
          <li key={`${s.billId}-${s.reason}`} className="flex gap-2 items-start px-3 py-1.5 text-xs">
            <span className="shrink-0 mt-0.5">{icon}</span>
            <span className="min-w-0">
              <span className="font-medium text-gray-800">{label(s.billId)}</span>
              <span className={tone === 'red' ? 'text-red-600' : 'text-gray-500'}> — {s.reason}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
